import { PageDataHandler } from "@/lib/utility/data/page-data-handler";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { $t } from "@/lib/utility/t";
import { PageFilterElement } from "@/models/page-filter-element.entity";
import { ISignDocument, SignDocument } from "@/models/sign-document.entity";
import store from "@/store/VuexPlugin";
import { PredefinedFilterType } from "@/views/event/filter/event.filter";
import { Module, getModule } from "vuex-module-decorators";
import { ActionEnum } from "../enum/authActionEnum";
import { BackendResourceEnum } from "../enum/authResourceEnum";
import { PaginatedBaseStore } from "../paginated-base.store";
import { SignDocumentControllerFindAllParamsGen } from "./../../services/sign/v1/data-contracts";
import { SignDocumentDataAccessLayer } from "./access-layers/sign-document.access-layer";
import { PaginationFilterListElement } from "./base-pagination.store";
import { UserModule } from "./me-user.store";
import { SignDocumentPageDataProvider } from "./page-data-provider/sign-document.page-data-provider";

/**
 * The reference types a document is most commonly linked to.
 * Used to offer quick filters on the document tables.
 */
export const DOCUMENT_QUICK_FILTER_REF_TYPES = [
  BackendResourceEnum.INVOICE,
  BackendResourceEnum.REPAIR_ESTIMATE,
  BackendResourceEnum.APPRAISAL,
  BackendResourceEnum.RENTAL
];

/**
 * Quick filters that narrow the documents down to those linked to a given reference type,
 * e.g. all documents that belong to an invoice.
 *
 * Only returns a quick filter for ref types the current user has READ permission on for
 * the given partnerId, so a user without e.g. Invoice access never sees an Invoice filter.
 *
 * Returns new filter elements on every call so that a table editing them does not alter the
 * filters of another table.
 */
export const documentRefTypeQuickFilters = (partnerId: string): PredefinedFilterType[] =>
  DOCUMENT_QUICK_FILTER_REF_TYPES.filter((refType) =>
    UserModule.abilities.can(ActionEnum.READ, refType, partnerId)
  ).map((refType) => ({
    name: `BackendResourceEnum.${refType}`,
    filter: [
      new PageFilterElement({
        key: "refs.refType",
        operation: PageFilterOperationEnum.EQUAL,
        value: refType
      })
    ]
  }));

@Module({
  dynamic: true,
  namespaced: true,
  name: "document",
  store
})
export class DocumentPaginationStore extends PaginatedBaseStore<ISignDocument, SignDocumentControllerFindAllParamsGen> {
  protected _data = SignDocumentDataAccessLayer;
  protected _pageProvider = SignDocumentPageDataProvider;
  protected _pager = new PageDataHandler(this._data, this._pageProvider);

  get commonPredefinedFilter(): PredefinedFilterType[] {
    return [
      {
        name: $t("sign.DocumentDetail.template"),
        filter: [
          new PageFilterElement({
            key: "isTemplate",
            operation: PageFilterOperationEnum.EQUAL,
            value: true as any
          })
        ]
      },
      {
        name: $t("sign.DocumentDetail.file"),
        filter: [
          new PageFilterElement({
            key: "isTemplate",
            operation: PageFilterOperationEnum.EQUAL,
            value: false as any
          }),
          new PageFilterElement({
            key: "isSigned",
            operation: PageFilterOperationEnum.EQUAL,
            value: false as any
          })
        ]
      },
      {
        name: $t("objects.document.isSigned"),
        filter: [
          new PageFilterElement({
            key: "isSigned",
            operation: PageFilterOperationEnum.EQUAL,
            value: true as any
          })
        ]
      }
    ];
  }

  filterOptions: PaginationFilterListElement[] = SignDocument.filterables;
}

export const DocumentModule = getModule(DocumentPaginationStore);
